import React, { useState } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Typography,
  Table,
  Space,
  Alert,
  message,
  Tag,
  Modal,
  Descriptions,
  Badge,
  Spin
} from 'antd';
import {
  SaveOutlined,
  ReloadOutlined,
  SettingOutlined,
  CrownOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { InputNumber, Switch } from 'antd';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, TierLimitationsResponse, TierLimitation } from '../lib/api';

const { Title, Text } = Typography;

const SubscriptionTierLimitations: React.FC = () => {
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState<string | null>(null);
  const [editingCell, setEditingCell] = useState<{tier: string, feature: string} | null>(null);
  const [editedValues, setEditedValues] = useState<Record<string, Record<string, any>>>({});
  const queryClient = useQueryClient();

  // Fetch tier limitations from backend
  const { 
    data: tierLimitationsData, 
    isLoading, 
    error, 
    refetch 
  } = useQuery<TierLimitationsResponse>({
    queryKey: ['tier-limitations'],
    queryFn: () => adminApi.getTierLimitations(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Mutation for updating tier limitations
  const updateLimitationsMutation = useMutation({
    mutationFn: (tierLimitations: Record<string, TierLimitation>) => 
      adminApi.updateTierLimitations(tierLimitations, 'admin'),
    onSuccess: () => {
      message.success('Tier limitations updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['tier-limitations'] });
    },
    onError: (error) => {
      console.error('Failed to update tier limitations:', error);
      message.error('Failed to update tier limitations. Please try again.');
    }
  });

  const handleSave = async () => {
    try {
      if (tierLimitationsData?.data?.tier_limitations) {
        // Merge edited values with original data
        const updatedLimitations = { ...tierLimitationsData.data.tier_limitations };
        
        Object.keys(editedValues).forEach(tier => {
          if (updatedLimitations[tier]) {
            updatedLimitations[tier] = {
              ...updatedLimitations[tier],
              ...editedValues[tier]
            };
          }
        });
        
        await updateLimitationsMutation.mutateAsync(updatedLimitations);
        setEditedValues({});
      }
    } catch (error) {
      console.error('Failed to save limitations:', error);
    }
  };

  const handleCellEdit = (tier: string, feature: string, value: any) => {
    setEditedValues(prev => ({
      ...prev,
      [tier]: {
        ...prev[tier],
        [feature]: value
      }
    }));
  };

  const getCurrentValue = (tier: string, feature: string) => {
    if (editedValues[tier] && editedValues[tier][feature] !== undefined) {
      return editedValues[tier][feature];
    }
    return (tierLimitationsData?.data?.tier_limitations as any)?.[tier]?.[feature];
  };

  const renderEditableCell = (tier: string, feature: string, value: any) => {
    const currentValue = getCurrentValue(tier, feature);
    const isEditing = editingCell?.tier === tier && editingCell?.feature === feature;
    const hasChanges = editedValues[tier] && editedValues[tier][feature] !== undefined;

    if (isEditing) {
      if (typeof currentValue === 'boolean') {
        return (
          <Switch
            checked={currentValue}
            onChange={(checked) => {
              handleCellEdit(tier, feature, checked);
              // Auto-close editing for boolean values
              setTimeout(() => setEditingCell(null), 100);
            }}
            autoFocus
          />
        );
      } else if (typeof currentValue === 'number' || currentValue === null) {
        return (
          <InputNumber
            value={currentValue === null ? undefined : currentValue}
            onChange={(val) => handleCellEdit(tier, feature, val)}
            onPressEnter={() => setEditingCell(null)}
            onBlur={() => setEditingCell(null)}
            placeholder="null = unlimited"
            style={{ width: '100%' }}
            autoFocus
          />
        );
      }
    }

    return (
      <div
        style={{ 
          cursor: 'pointer', 
          minHeight: '32px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: hasChanges ? '#fff2e6' : 'transparent',
          borderRadius: '4px',
          padding: '4px'
        }}
        onClick={() => setEditingCell({ tier, feature })}
      >
        {formatValue(currentValue, feature)}
      </div>
    );
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleResetChanges = () => {
    setEditedValues({});
    setEditingCell(null);
    message.info('Changes reset');
  };

  const hasUnsavedChanges = Object.keys(editedValues).length > 0;

  const getTierColor = (tier: string) => {
    const colors: Record<string, string> = {
      'free': '#8c8c8c',
      'bronze': '#CD7F32',
      'silver': '#C0C0C0', 
      'gold': '#FFD700',
      'firooze': '#40E0D0'
    };
    return colors[tier] || '#1890ff';
  };

  const formatValue = (value: number | boolean | null | undefined, feature: string) => {
    if (value === null || value === undefined) {
      return <Tag color="green">Unlimited</Tag>;
    }
    if (typeof value === 'boolean') {
      return value ? (
        <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
      ) : (
        <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '16px' }} />
      );
    }
    if (typeof value === 'number') {
      if (value === -1) {
        return <Tag color="green">Unlimited</Tag>;
      }
      if (value === 0 && feature.includes('batch')) {
        return <Tag color="red">Disabled</Tag>;
      }
      return <Tag color="blue">{value}</Tag>;
    }
    return <Tag>{String(value)}</Tag>;
  };

  // Create columns for the table
  const columns = [
    {
      title: 'Feature',
      dataIndex: 'feature',
      key: 'feature',
      width: 250,
      render: (text: string, record: any) => (
        <div>
          <Text strong>{text.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</Text>
          {tierLimitationsData?.data?.feature_descriptions?.[text] && (
            <div>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {tierLimitationsData.data.feature_descriptions[text]}
              </Text>
            </div>
          )}
        </div>
      )
    },
    {
      title: <span style={{ color: getTierColor('free') }}>Free</span>,
      dataIndex: 'free',
      key: 'free',
      align: 'center' as const,
      render: (value: any, record: any) => renderEditableCell(
        'free', 
        record.feature, 
        (tierLimitationsData?.data?.tier_limitations?.free as any)?.[record.feature]
      )
    },
    {
      title: <span style={{ color: getTierColor('bronze') }}>Bronze</span>,
      dataIndex: 'bronze',
      key: 'bronze',
      align: 'center' as const,
      render: (value: any, record: any) => renderEditableCell(
        'bronze', 
        record.feature, 
        (tierLimitationsData?.data?.tier_limitations?.bronze as any)?.[record.feature]
      )
    },
    {
      title: <span style={{ color: getTierColor('silver') }}>Silver</span>,
      dataIndex: 'silver',
      key: 'silver',
      align: 'center' as const,
      render: (value: any, record: any) => renderEditableCell(
        'silver', 
        record.feature, 
        (tierLimitationsData?.data?.tier_limitations?.silver as any)?.[record.feature]
      )
    },
    {
      title: <span style={{ color: getTierColor('gold') }}>Gold</span>,
      dataIndex: 'gold',
      key: 'gold',
      align: 'center' as const,
      render: (value: any, record: any) => renderEditableCell(
        'gold', 
        record.feature, 
        (tierLimitationsData?.data?.tier_limitations?.gold as any)?.[record.feature]
      )
    },
    {
      title: <span style={{ color: getTierColor('firooze') }}>Firooze</span>,
      dataIndex: 'firooze',
      key: 'firooze',
      align: 'center' as const,
      render: (value: any, record: any) => renderEditableCell(
        'firooze', 
        record.feature, 
        (tierLimitationsData?.data?.tier_limitations?.firooze as any)?.[record.feature]
      )
    },
    {
      title: 'Action',
      key: 'action',
      align: 'center' as const,
      width: 100,
      render: (text: any, record: any) => (
        <Button 
          type="text" 
          icon={<EyeOutlined />}
          onClick={() => {
            setSelectedFeature(record.feature);
            setDetailModalVisible(true);
          }}
        >
          View
        </Button>
      )
    }
  ];

  // Create data source from tier limitations
  const dataSource = React.useMemo(() => {
    if (!tierLimitationsData?.data?.tier_limitations) return [];
    
    const features = new Set<string>();
    Object.values(tierLimitationsData.data.tier_limitations).forEach(tier => {
      Object.keys(tier).forEach(feature => features.add(feature));
    });
    
    return Array.from(features).map(feature => ({
      key: feature,
      feature: feature,
    }));
  }, [tierLimitationsData]);

  if (error) {
    return (
      <Alert
        message="Error Loading Tier Limitations"
        description="Failed to load tier limitations. Please check your connection and try again."
        type="error"
        action={
          <Button size="small" danger onClick={handleRefresh}>
            <ReloadOutlined /> Retry
          </Button>
        }
      />
    );
  }

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
            <div>
              <Title level={3} style={{ margin: 0 }}>
                <SettingOutlined style={{ marginRight: '8px' }} />
                Subscription Tier Limitations
              </Title>
              <Text type="secondary">
                Manage feature limitations and quotas for each subscription tier
              </Text>
              <br />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                💡 Click on any cell value to edit it. Changes will be highlighted in orange.
              </Text>
            </div>
            <Space>
              <Button 
                icon={<ReloadOutlined />} 
                onClick={handleRefresh}
                loading={isLoading}
              >
                Refresh
              </Button>
              {hasUnsavedChanges && (
                <Button 
                  icon={<CloseCircleOutlined />}
                  onClick={handleResetChanges}
                  danger
                >
                  Reset Changes
                </Button>
              )}
              <Button 
                type="primary" 
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={updateLimitationsMutation.isPending}
                disabled={!hasUnsavedChanges}
              >
                {hasUnsavedChanges ? 'Save Changes' : 'No Changes'}
              </Button>
            </Space>
          </Space>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card 
            title={
              <Space>
                <CrownOutlined />
                Tier Limitations Overview
                <Badge count={dataSource.length} style={{ backgroundColor: '#1890ff' }} />
              </Space>
            }
          >
            <Spin spinning={isLoading}>
              <Table
                dataSource={dataSource}
                columns={columns}
                rowKey="feature"
                pagination={false}
                size="small"
                scroll={{ x: 800 }}
              />
            </Spin>
          </Card>
        </Col>
      </Row>

      {/* Feature Detail Modal */}
      <Modal
        title={`Feature Details: ${selectedFeature?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`}
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>
        ]}
        width={700}
      >
        {selectedFeature && tierLimitationsData?.data && (
          <div>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Feature Key">
                <Text code>{selectedFeature}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Description">
                {tierLimitationsData.data.feature_descriptions?.[selectedFeature] || 'No description available'}
              </Descriptions.Item>
              <Descriptions.Item label="API Endpoints">
                {tierLimitationsData.data.api_endpoints?.[selectedFeature] ? (
                  <div>
                    {tierLimitationsData.data.api_endpoints[selectedFeature].map((endpoint, idx) => (
                      <div key={idx}>
                        <Text code>{endpoint}</Text>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Text type="secondary">No specific endpoints</Text>
                )}
              </Descriptions.Item>
            </Descriptions>

            <div style={{ marginTop: '16px' }}>
              <Title level={5}>Tier Values:</Title>
              <Row gutter={[8, 8]}>
                {['free', 'bronze', 'silver', 'gold', 'firooze'].map(tier => {
                  const value = (tierLimitationsData.data.tier_limitations as any)?.[tier]?.[selectedFeature];
                  return (
                    <Col key={tier} span={24}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        padding: '8px 12px',
                        border: '1px solid #f0f0f0',
                        borderRadius: '6px',
                        backgroundColor: '#fafafa'
                      }}>
                        <Text strong style={{ color: getTierColor(tier) }}>
                          {tier.charAt(0).toUpperCase() + tier.slice(1)}:
                        </Text>
                        {formatValue(value, selectedFeature)}
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SubscriptionTierLimitations;